import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type Language } from '@models/i18n/language.model';

const trimTrailingSlash = (pathname: string): string => {
  if (pathname === '/') {
    return pathname;
  }

  // Remove trailing slashes without regex to prevent ReDoS
  let end = pathname.length;
  while (end > 0 && pathname[end - 1] === '/') {
    end--;
  }
  return end === pathname.length ? pathname : pathname.slice(0, end);
};

export const isSupportedLanguageSegment = (value: string | undefined): value is Language =>
  typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value as Language);

export const getLanguageFromPathname = (pathname: string): Language => {
  const segments = trimTrailingSlash(pathname).split('/').filter(Boolean);
  const firstSegment = segments.at(0);

  return isSupportedLanguageSegment(firstSegment) ? firstSegment : DEFAULT_LANGUAGE;
};

export const setPathLanguage = (pathname: string, language: Language): string => {
  const segments = trimTrailingSlash(pathname).split('/').filter(Boolean);

  if (segments.length === 0) {
    return `/${language}`;
  }

  if (isSupportedLanguageSegment(segments.at(0))) {
    segments[0] = language;
    return `/${segments.join('/')}`;
  }

  return `/${[language, ...segments].join('/')}`;
};

export const normalizeLanguagePath = (pathname: string): string => {
  const normalizedPathname = trimTrailingSlash(pathname);
  const segments = normalizedPathname.split('/').filter(Boolean);

  if (isSupportedLanguageSegment(segments.at(0))) {
    return normalizedPathname;
  }

  return `/${DEFAULT_LANGUAGE}${normalizedPathname === '/' ? '' : normalizedPathname}`;
};

/**
 * Rewrites the URL bar to reflect the new language segment.
 * Uses replaceState — no page reload, no router remount.
 */
export const syncUrlToLanguage = (language: Language): void => {
  const newPath = `${setPathLanguage(window.location.pathname, language)}${window.location.search}${window.location.hash}`;
  window.history.replaceState(null, '', newPath);
};
/**
 * Extracts a supported language code from an anchor's href.
 * Returns null when the href does not begin with a known language segment.
 */
export const extractLanguageFromHref = (href: string): Language | null => {
  const firstSegment = href.replace(/^\//, '').split('/')[0];
  return isSupportedLanguageSegment(firstSegment) ? firstSegment : null;
};
